import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from '@hooks/useTranslation';
import CloseIcon from '@assets/icons/CloseIcon';
import CheckIcon from '@assets/icons/CheckIcon';
import NotificationContainer from '@components/shared/NotificationContainer/NotificationContainer';
import styles from './FullPageStepper.module.scss';

// ============================================
// TYPES
// ============================================

/** Configuration for a single step in the full-page stepper. */
export interface FullPageStepperStep {
  /** Unique step identifier */
  id: string;
  /** Step label shown in the sidebar navigation */
  label: string;
  /** Step title shown in the content area header */
  title: string;
  /** Optional description text below the step title */
  description?: string;
  /** Optional saved status — renders a "Saved" badge next to the label in the sidebar */
  saved?: boolean;
}

/** Props for the FullPageStepper component. */
export interface FullPageStepperProps {
  /** Page title shown in the header bar. Accepts a string or a composite ReactNode (e.g., title + inline context chips). */
  pageTitle: string | ReactNode;
  /** Steps configuration array */
  steps: FullPageStepperStep[];
  /** Currently active step index (0-based) */
  activeStep: number;
  /** Called when the close (X) button is clicked */
  onClose: () => void;
  /** Hide the close (X) button in the header */
  hideCloseButton?: boolean;
  /** Called when a completed step is clicked in the sidebar */
  onStepClick?: (stepIndex: number) => void;
  /** Optional right-side header content (e.g., "Need Help?" link) */
  headerAction?: ReactNode;
  /** Step content — the form/content for the current step */
  children: ReactNode;
  /** Footer content — typically navigation buttons (Continue, Back) */
  footer?: ReactNode;
  /** Optional className for the root element */
  className?: string;
  /** Optional max-width for the content inner area (default: '70%') */
  contentMaxWidth?: string;
  /**
   * Content sizing variant.
   * - `'default'` — outer content area scrolls; inner content grows naturally.
   * - `'fullHeight'` — outer content is fixed-height (`overflow: hidden`); children receive a
   *   definite height so an inner scroll region (e.g. PDF viewer) can engage its own scroll.
   */
  contentVariant?: 'default' | 'fullHeight';
}

type StepStatus = 'completed' | 'active' | 'upcoming';

// ============================================
// HELPERS
// ============================================

function getStepStatus(stepIndex: number, activeStep: number): StepStatus {
  if (stepIndex < activeStep) return 'completed';
  if (stepIndex === activeStep) return 'active';
  return 'upcoming';
}

// ============================================
// STEP INDICATOR (sidebar)
// ============================================

interface StepIndicatorProps {
  step: FullPageStepperStep;
  index: number;
  activeStep: number;
  totalSteps: number;
  onStepClick?: (stepIndex: number) => void;
}

function StepIndicator({ step, index, activeStep, totalSteps, onStepClick }: StepIndicatorProps) {
  const { t } = useTranslation();
  const status = getStepStatus(index, activeStep);
  const isClickable = status === 'completed' && Boolean(onStepClick);
  const isLast = index === totalSteps - 1;

  const circleClass = [
    styles.circle,
    status === 'completed' ? styles.circleCompleted : '',
    status === 'active' ? styles.circleActive : '',
    status === 'upcoming' ? styles.circleUpcoming : '',
    isClickable ? styles.circleClickable : '',
  ]
    .filter(Boolean)
    .join(' ');

  const labelClass = [
    styles.stepLabel,
    status === 'active' ? styles.stepLabelActive : '',
    status === 'completed' ? styles.stepLabelCompleted : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li className={styles.stepItem}>
      <div className={styles.stepRow}>
        {/* Circle */}
        <button
          type="button"
          className={circleClass}
          onClick={isClickable ? () => onStepClick!(index) : undefined}
          disabled={!isClickable}
          aria-current={status === 'active' ? 'step' : undefined}
          aria-label={`${step.label}${status === 'completed' ? ' (completed)' : status === 'active' ? ' (current)' : ''}`}
          tabIndex={isClickable ? 0 : -1}
        >
          {status === 'completed' ? (
            <span className={styles.checkIcon} aria-hidden="true">
              <CheckIcon />
            </span>
          ) : (
            <span aria-hidden="true">{index + 1}</span>
          )}
        </button>

        {/* Label */}
        <span className={labelClass}>
          <span className={styles.stepLabelText} title={step.label}>{step.label}</span>
          {step.saved && <span className={styles.savedBadge}>{t('K_BADGE_SAVED', 'Saved')}</span>}
        </span>
      </div>

      {/* Connecting bar below circle (not on last step) */}
      {!isLast && (
        <div
          className={`${styles.connector} ${index < activeStep ? styles.connectorCompleted : ''}`}
          aria-hidden="true"
        />
      )}
    </li>
  );
}

// ============================================
// COMPONENT
// ============================================

/**
 * FullPageStepper renders a full-viewport multi-step form layout
 * without the app sidebar/topnav. Designed for onboarding flows
 * and multi-step entity creation pages.
 *
 * It renders three regions:
 * 1. A fixed header bar with close button, title, and optional action slot
 * 2. A vertical step sidebar (left) showing progress
 * 3. A scrollable content area (right) with step badge, title, description, children, and footer
 *
 * State management is external — use the `useMultiStepForm` hook
 * to drive `activeStep` and wire up navigation buttons in `footer`.
 */
function FullPageStepper({
  pageTitle,
  steps,
  activeStep,
  onClose,
  hideCloseButton = false,
  onStepClick,
  headerAction,
  children,
  footer,
  className,
  contentMaxWidth,
  contentVariant = 'default',
}: FullPageStepperProps) {
  const { t } = useTranslation();
  const currentStep = useMemo(() => steps[activeStep], [steps, activeStep]);
  const totalSteps = steps.length;

  return (
    <div className={`${styles.root} ${className ?? ''}`.trim()}>
      <NotificationContainer />
      {/* ---- Header bar ---- */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          {!hideCloseButton && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          )}
          <h1 className={styles.headerTitle}>{pageTitle}</h1>
        </div>
        {headerAction && <div className={styles.headerRight}>{headerAction}</div>}
      </header>

      {/* ---- Body: sidebar + content ---- */}
      <div className={styles.body}>
        {/* Sidebar step nav */}
        <nav className={styles.sidebar} aria-label="Steps">
          <ol className={styles.stepList}>
            {steps.map((step, index) => (
              <StepIndicator
                key={step.id}
                step={step}
                index={index}
                activeStep={activeStep}
                totalSteps={totalSteps}
                onStepClick={onStepClick}
              />
            ))}
          </ol>
        </nav>

        {/* Content area */}
        <main
          className={`${styles.content} ${contentVariant === 'fullHeight' ? styles.contentFullHeight : ''}`.trim()}
        >
          <div
            className={`${styles.contentInner} ${contentVariant === 'fullHeight' ? styles.contentInnerFullHeight : ''}`.trim()}
            style={contentMaxWidth ? { maxWidth: contentMaxWidth } : undefined}
          >
            {/* Step badge + title */}
            <div className={styles.contentHeader}>
              <span className={styles.stepBadge}>
                {t('K_STEP_PROGRESS', 'Step {current} of {total}').replace('{current}', String(activeStep + 1)).replace('{total}', String(totalSteps))}
              </span>
              {currentStep && (
                <>
                  <h2 className={styles.contentTitle}>{currentStep.title}</h2>
                  {currentStep.description && (
                    <p className={styles.contentDescription}>{currentStep.description}</p>
                  )}
                </>
              )}
            </div>

            {/* Step form content */}
            <div className={styles.contentBody}>{children}</div>

            {/* Footer actions */}
            {footer && <div className={styles.contentFooter}>{footer}</div>}
          </div>
        </main>
      </div>
    </div>
  );
}

export default FullPageStepper;
